import { CropOverlay } from '@/components/app/editor/tools/crop-overlay'
import { CropToolbar } from '@/components/app/editor/tools/crop-toolbar'
import { cropShapeUnchangedFromOriginal } from '../helpers/edit-mode-unchanged'
import { EditModeDimOverlay } from './edit-mode-dim-overlay'
import type {
  CanvasEditModeHandlers,
  CanvasEditModes,
  CanvasFoundation,
  CanvasViewport,
} from '../types/canvas-groups'
import type { ResolvedCenterCanvasConfig } from '../types/resolved-center-canvas-config'

/**
 * Crop mode's chrome for the shape being cropped: the dim overlay, the resizable frame overlay
 * and the floating toolbar (its zoom slider renders separately, after the Text on a Path bar).
 * Renders nothing while `shape` is null (not cropping, or the shape's geometry is locked/frozen).
 */
export function CropModeChrome({
  config,
  foundation,
  viewport,
  editModes,
  editModeHandlers,
}: {
  config: Pick<ResolvedCenterCanvasConfig, 'bleedWidth' | 'bleedHeight'>
  foundation: Pick<CanvasFoundation, 'documentCanvasRef'>
  viewport: Pick<CanvasViewport, 'scale'>
  editModes: Pick<CanvasEditModes, 'cropOverlayRef' | 'cropToolbarRef' | 'cropOriginalShapeRef'>
  editModeHandlers: Pick<
    CanvasEditModeHandlers,
    | 'cropChromeShape'
    | 'cropDimGeom'
    | 'handleCropFrameChange'
    | 'handleCropFrameCommit'
    | 'handlePhotoPanChange'
    | 'handlePhotoPanCommit'
    | 'handleShapeRotateChange'
    | 'handleShapeRotateCommit'
    | 'handleCropReset'
  >
}) {
  const { bleedWidth, bleedHeight } = config
  const { documentCanvasRef } = foundation
  const { scale } = viewport
  const { cropOverlayRef, cropToolbarRef, cropOriginalShapeRef } = editModes
  const {
    cropChromeShape: shape,
    cropDimGeom: dimGeom,
    handleCropFrameChange: onFrameChange,
    handleCropFrameCommit: onFrameCommit,
    handlePhotoPanChange: onPhotoPanChange,
    handlePhotoPanCommit: onPhotoPanCommit,
    handleShapeRotateChange: onShapeRotateChange,
    handleShapeRotateCommit: onShapeRotateCommit,
    handleCropReset: onReset,
  } = editModeHandlers

  if (!shape) return null

  return (
    <>
      {/* Crop Dim Overlay — same fade-the-rest-of-the-artboard treatment as the
          node-edit dim overlay, so the shape being cropped stands out. */}
      {dimGeom && <EditModeDimOverlay variant="crop" shape={shape} geom={dimGeom} />}
      {/* Crop Overlay — whole-shape resizable frame + drag-to-pan, shown when
          "Crop Photo" is chosen from the photo context menu. */}
      <CropOverlay
        ref={cropOverlayRef}
        shape={shape}
        bleedWidth={bleedWidth}
        bleedHeight={bleedHeight}
        scale={scale}
        onFrameChange={onFrameChange}
        onFrameCommit={onFrameCommit}
        onPhotoPanChange={onPhotoPanChange}
        onPhotoPanCommit={onPhotoPanCommit}
        onRotateChange={onPhotoPanChange}
        onRotateCommit={onPhotoPanCommit}
        onShapeRotateChange={onShapeRotateChange}
        onShapeRotateCommit={onShapeRotateCommit}
      />
      {/* Crop Toolbar — floating pill above/below/beside the shape being cropped */}
      <CropToolbar
        ref={cropToolbarRef}
        shape={shape}
        scale={scale}
        canvasRef={documentCanvasRef}
        onFlipVertical={() => {
          if (!shape.photoFill) return
          onPhotoPanCommit(shape.id, {
            ...shape.photoFill,
            flippedY: !shape.photoFill.flippedY,
          })
        }}
        onFlipHorizontal={() => {
          if (!shape.photoFill) return
          onPhotoPanCommit(shape.id, {
            ...shape.photoFill,
            flippedX: !shape.photoFill.flippedX,
          })
        }}
        onReset={onReset}
        resetDisabled={cropShapeUnchangedFromOriginal(shape, cropOriginalShapeRef.current)}
      />
    </>
  )
}
